import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import TextArea from "@/common/components/TextArea";
import { ShowHeader } from "@/common/components/ShowHeader";
import { UnitTextField } from "@/common/components/UnitTextField";
import { DateInput } from "@/common/components/DateInput/DateInput";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { createSchema, TmaisRafflesValidationSchema } from "../../schemas/tmaisRaffles.schema";
import useCreateTmaisRaffle from "../../hooks/useCreateTmaisRaffle"; 
import { winnerOperationKlassOptions } from "../../constants/winnerOperationKlassOptions.constant";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader";
import { numberOfTicketsOptions } from "../../constants/numberOfTicketsOptions.constant";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";
import MaskedTextField from "@/common/components/MaskedTextField";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { timeMask } from "@/common/constants/masks.constant";

const Create = () => {
  const queryClient = useQueryClient()
  const goBack = useGoBack()
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs();

  const { data: company } = useMineCompany();

  const {
    mutate,
    isPending: createRaffleIsPending
  } = useCreateTmaisRaffle({
    establishmentId: company?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['tmaisRaffles']})
      goBack({fallback: `/tmais-raffles`})
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError })(error)
      }
    }
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToCreateTmaisRaffle =
    isUserPermissionsPending || hasUserPermission('Establishment::Tmais::Raffles::Create')

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<TmaisRafflesValidationSchema>({
    resolver: zodResolver(createSchema)
  });

  const onSubmit: SubmitHandler<TmaisRafflesValidationSchema> = (data) => mutate(data);

  return (
    <form
      className="flex flex-col h-[calc(100vh-64px)]"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="false"
    >
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/tmais-raffles` }}
        title="Sorteios"
        subtitle="Cadastrar sorteio"
        buttons={(
          <div className="flex items-center gap-2">
            <Button
              variant='outline-error'
              onClick={() => goBack({fallback: `/tmais-raffles`})}
            >
              Cancelar
            </Button>
            {userHasPermissionToCreateTmaisRaffle && (
              <Button
                type="submit"
                name="submit"
              >
                Cadastrar sorteio
              </Button>
            )}
          </div>
        )}
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={userHasPermissionToCreateTmaisRaffle ? 200 : 403}
      >
        <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <h2 className="text-lg font-bold">Detalhes do Sorteio</h2>
          <h3 className="text-base font-bold">Nome e imagens do sorteio</h3>

          <div className="flex gap-4">
            <MultipleImageUploader
              label="Anexe uma foto"
              name="pictures_attachments_attributes"
              accept="image/jpeg, image/png"
              uploadBlob={uploadBlob}
              containerClassName="max-w-[288px]"
              control={control}
              fieldName="pictures_attachments_attributes"
              disabled={uploadBlobIsPending || createRaffleIsPending}
              errorMessage={errors.pictures_attachments_attributes?.message || undefined}
              uploadButtonLayout="floating"
            />
            <div className="flex flex-col gap-4 w-full">
              <TextField
                label="Nome"
                {...register('display_name')}
                errorMessage={errors?.display_name?.message}
                markAsRequired
                className="w-full"
              />
 
              <TextArea
                label="Descrição"
                markAsRequired
                errorMessage={errors?.description?.message}
                {...register('description')}
                rows={8}
              />
            </div>
          </div>

          <h2 className="text-base font-bold">Configurações do sorteio</h2>

          <div className="flex items-start gap-2">
            <UnitTextField
              label="Custo em pontos do bilhete"
              {...register('points_per_ticket')}
              errorMessage={errors?.points_per_ticket?.message}
              disabled={createRaffleIsPending}
              decimals={0}
              markAsRequired
              className="w-1/3"
            />
            <SelectController
              name="number_of_tickets"
              control={control}
              label="Quantidade de bilhetes"
              errorMessage={errors?.number_of_tickets?.message}
              isDisabled={createRaffleIsPending}
              options={numberOfTicketsOptions || []}
              markAsRequired
              className="w-1/3"
            />
            <UnitTextField
              label="Máximo de bilhetes por usuário"
              {...register('max_tickets_per_user')}
              errorMessage={errors?.max_tickets_per_user?.message}
              disabled={createRaffleIsPending}
              markAsRequired
              decimals={0}
              className="w-1/3"
            />
          </div>

          <div className="flex items-start gap-2">
            <SelectController
              name="select_winner_operation_klass"
              control={control}
              label="Forma do sorteio"
              options={winnerOperationKlassOptions}
              errorMessage={errors?.select_winner_operation_klass?.message}
              isDisabled={createRaffleIsPending}
              markAsRequired
              className="w-1/2"
            />
            <div className="flex flex-col w-1/2 gap-2">
              <div className="flex gap-2">
                <DateInput
                  fieldName="select_winner_at"
                  control={control}
                  label="Dia do sorteio"
                  markAsRequired
                  disabled={createRaffleIsPending}
                  className="w-1/2"
                />
                <MaskedTextField
                  mask={timeMask}
                  label='Hora do sorteio'
                  placeholder='00:00'
                  {...register('select_winner_time_at')}
                  className="w-1/2"
                />
              </div>
              {errors.select_winner_at?.message && <ErrorMessage name='select_winner_at' message={errors.select_winner_at.message} />}
            </div>
          </div>

          <div className="flex items-start gap-2">
            <DateInput
              fieldName="available_from"
              control={control}
              errors={errors}
              errorMessage={errors.available_from?.message || undefined}
              label="Data inicial para participação"
              markAsRequired
              disabled={createRaffleIsPending}
              className="w-1/2"
            />
            <DateInput
              fieldName="available_until"
              control={control}
              errors={errors}
              errorMessage={errors.available_until?.message || undefined}
              label="Data final para participação"
              markAsRequired
              disabled={createRaffleIsPending}
              className="w-1/2"
            />
          </div>
        </Box>
      </ErrorMessageWrapper>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default Create;
